import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { gtagPageView } from "./lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "./contexts/BookingContext";
import { CartProvider } from "./contexts/CartContext";
import { BuildLookProvider } from "./contexts/BuildLookContext";
import WishlistLoginPrompt from "./components/WishlistLoginPrompt";
import { CustomerAuthProvider } from "./contexts/CustomerAuthContext";
import { SavedLooksProvider } from "./contexts/SavedLooksContext";
import { SiteTextsProvider } from "./contexts/SiteTextsContext";
import DateSelectionModal from "./components/DateSelectionModal";
import ScrollToTop from "./components/ScrollToTop";
import ImageProtection from "./components/ImageProtection";
import Home from "./pages/Home";
import OurStory from "./pages/OurStory";
import FAQ from "./pages/FAQ";
import BorrowingTerms from "./pages/BorrowingTerms";
import Contact from "./pages/Contact";
import Catalog from "./pages/Catalog";
import OutfitDetail from "./pages/OutfitDetail";
import BookingPage from "./pages/BookingPage";
import BookingSuccess from "./pages/BookingSuccess";
import PaymentPending from "./pages/PaymentPending";
import CustomerLogin from "./pages/CustomerLogin";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import CustomerAccount from "./pages/CustomerAccount";
import CustomerBookings from "./pages/CustomerBookings";
import BookingReceipt from "./pages/BookingReceipt";
import PaymentMethods from "./pages/PaymentMethods";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOutfits from "./pages/AdminOutfits";
import AdminBookings from "./pages/AdminBookings";
import AdminSettings from "./pages/AdminSettings";
import AdminSocial from "./pages/AdminSocial";
import AdminBatchUpload from "./pages/AdminBatchUpload";
import AdminDeliverySettings from "./pages/AdminDeliverySettings";
import AdminHeroSettings from "./pages/AdminHeroSettings";
import AdminSiteTexts from "./pages/AdminSiteTexts";
import AdminSizeGuide from "./pages/AdminSizeGuide";
import AdminCategories from "./pages/AdminCategories";
import AdminFeatureToggles from "./pages/AdminFeatureToggles";
import AdminEventTypes from "./pages/AdminEventTypes";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminBusinessReviews from "./pages/admin/AdminBusinessReviews";
import AdminReviewRequests from "./pages/admin/AdminReviewRequests";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminTrialReminderSettings from "./pages/admin/AdminTrialReminderSettings";
import AdminCommunications from "./pages/admin/AdminCommunications";
import AdminAnalytics from "./pages/AdminAnalytics";

import CatalogueEditMode from "./pages/CatalogueEditMode";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import SavedLooksPage from "./pages/SavedLooksPage";
import CartConfirmation from "./components/CartConfirmation";
import AppointmentBooking from "./pages/AppointmentBooking";
import AdminAppointmentSettings from "./pages/AdminAppointmentSettings";
import AdminAppointments from "./pages/AdminAppointments";
import AdminCalendar from "./pages/AdminCalendar";
import AdminBookingTypes from "./pages/AdminBookingTypes";
import AdminTrialLeads from "./pages/AdminTrialLeads";
import AdminTrialSlots from "./pages/AdminTrialSlots";
import AdminCustomers from "./pages/AdminCustomers";
import AdminCustomerDetail from "./pages/AdminCustomerDetail";
import TrialBooking from "./pages/TrialBooking";
import TrialBookingSuccess from "./pages/TrialBookingSuccess";
import AttendanceConfirm from "./pages/AttendanceConfirm";
import AdminEmailQueue from "./pages/AdminEmailQueue";
import AdminAuditLog from "./pages/AdminAuditLog";
import AdminCosts from "./pages/AdminCosts";
import AdminRentalCredits from "./pages/AdminRentalCredits";
import AdminTrialAnalytics from "./pages/AdminTrialAnalytics";
import AdminFAQs from "./pages/AdminFAQs";
import LooksPage from "./pages/LooksPage";
import LookDetail from "./pages/LookDetail";
import AdminLooksManager from "./pages/AdminLooksManager";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import BuildYourLookPage from "./pages/BuildYourLookPage";
import MyStyledLooksPage from "./pages/MyStyledLooksPage";
import IndianOutfitHireMelbourne from "./pages/IndianOutfitHireMelbourne";
import LehengaHireMelbourne from "./pages/LehengaHireMelbourne";
import SareeRentalMelbourne from "./pages/SareeRentalMelbourne";
import { AdminErrorBoundary } from "./components/admin/AdminErrorBoundary";

function AdminRoute({ children }) {
  return <AdminErrorBoundary>{children}</AdminErrorBoundary>;
}

function AppRouter() {
  const location = useLocation();
  // SPA page view tracking for GA4 / Google Ads
  useEffect(() => { gtagPageView(location.pathname, document.title); }, [location.pathname]);
  // Check URL fragment for session_id from Google OAuth before rendering routes
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/our-story" element={<OurStory />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/borrowing-terms" element={<BorrowingTerms />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/catalog" element={<Catalog />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/saved-looks" element={<SavedLooksPage />} />
      <Route path="/build-your-look" element={<BuildYourLookPage />} />
      <Route path="/looks" element={<LooksPage />} />
      <Route path="/looks/:id" element={<LookDetail />} />
      <Route path="/indian-outfit-hire-melbourne" element={<IndianOutfitHireMelbourne />} />
      <Route path="/lehenga-hire-melbourne" element={<LehengaHireMelbourne />} />
      <Route path="/saree-rental-melbourne" element={<SareeRentalMelbourne />} />
      <Route path="/:parentSlug" element={<Catalog />} />
      <Route path="/:parentSlug/:childSlug" element={<Catalog />} />
      <Route path="/outfit/:id" element={<OutfitDetail />} />
      <Route path="/booking/:id" element={<BookingPage />} />
      <Route path="/booking/success" element={<BookingSuccess />} />
      <Route path="/booking/appointments" element={<AppointmentBooking />} />
      <Route path="/booking/pending/:bookingId" element={<PaymentPending />} />
      <Route path="/login" element={<CustomerLogin />} />
      <Route path="/register" element={<CustomerLogin />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/account" element={<CustomerAccount />} />
      <Route path="/account/bookings" element={<CustomerBookings />} />
      <Route path="/account/bookings/:id" element={<BookingReceipt />} />
      <Route path="/account/payment-methods" element={<PaymentMethods />} />
      <Route path="/account/my-styled-looks" element={<MyStyledLooksPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/outfits" element={<AdminRoute><AdminOutfits /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
      <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/social" element={<AdminRoute><AdminSocial /></AdminRoute>} />
      <Route path="/admin/upload" element={<AdminRoute><AdminBatchUpload /></AdminRoute>} />
      <Route path="/admin/delivery-settings" element={<AdminRoute><AdminDeliverySettings /></AdminRoute>} />
      <Route path="/admin/hero-settings" element={<AdminRoute><AdminHeroSettings /></AdminRoute>} />
      <Route path="/admin/site-texts" element={<AdminRoute><AdminSiteTexts /></AdminRoute>} />
      <Route path="/admin/size-guide" element={<AdminRoute><AdminSizeGuide /></AdminRoute>} />
      <Route path="/admin/categories" element={<AdminRoute><AdminCategories /></AdminRoute>} />
      <Route path="/admin/feature-toggles" element={<AdminRoute><AdminFeatureToggles /></AdminRoute>} />
      <Route path="/admin/event-types" element={<AdminRoute><AdminEventTypes /></AdminRoute>} />
      <Route path="/admin/reviews" element={<AdminRoute><AdminReviews /></AdminRoute>} />
      <Route path="/admin/business-reviews" element={<AdminRoute><AdminBusinessReviews /></AdminRoute>} />
      <Route path="/admin/review-requests" element={<AdminRoute><AdminReviewRequests /></AdminRoute>} />
      <Route path="/admin/notifications" element={<AdminRoute><AdminNotifications /></AdminRoute>} />
      <Route path="/admin/communications" element={<AdminRoute><AdminCommunications /></AdminRoute>} />
      <Route path="/admin/appointments" element={<AdminRoute><AdminAppointments /></AdminRoute>} />
      <Route path="/admin/calendar" element={<AdminRoute><AdminCalendar /></AdminRoute>} />
      <Route path="/admin/booking-types" element={<AdminRoute><AdminBookingTypes /></AdminRoute>} />
      <Route path="/admin/trial-leads" element={<AdminRoute><AdminTrialLeads /></AdminRoute>} />
      <Route path="/admin/trial-slots" element={<AdminRoute><AdminTrialSlots /></AdminRoute>} />
      <Route path="/admin/trial-reminders" element={<AdminRoute><AdminTrialReminderSettings /></AdminRoute>} />
      <Route path="/admin/customers" element={<AdminRoute><AdminCustomers /></AdminRoute>} />
      <Route path="/admin/customers/:customerId" element={<AdminRoute><AdminCustomerDetail /></AdminRoute>} />
      <Route path="/admin/email-queue" element={<AdminRoute><AdminEmailQueue /></AdminRoute>} />
      <Route path="/admin/audit-log" element={<AdminRoute><AdminAuditLog /></AdminRoute>} />
      <Route path="/admin/costs" element={<AdminRoute><AdminCosts /></AdminRoute>} />
      <Route path="/admin/rental-credits" element={<AdminRoute><AdminRentalCredits /></AdminRoute>} />
      <Route path="/admin/analytics/trials" element={<AdminRoute><AdminTrialAnalytics /></AdminRoute>} />
      <Route path="/booking/trial" element={<TrialBooking />} />
      <Route path="/booking/trial/success" element={<TrialBookingSuccess />} />
      <Route path="/attendance/:token" element={<AttendanceConfirm />} />
      <Route path="/admin/appointment-settings" element={<AdminRoute><AdminAppointmentSettings /></AdminRoute>} />
      <Route path="/admin/faqs" element={<AdminRoute><AdminFAQs /></AdminRoute>} />
      <Route path="/admin/catalogue" element={<AdminRoute><CatalogueEditMode /></AdminRoute>} />
      <Route path="/admin/looks" element={<AdminRoute><AdminLooksManager /></AdminRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#FAF8F0] font-sans text-[#282828] antialiased">
      <Toaster position="top-center" />
      <BrowserRouter>
        <CustomerAuthProvider>
          <SavedLooksProvider>
            <SiteTextsProvider>
              <BookingProvider>
                <CartProvider>
                  <BuildLookProvider>
                    <ScrollToTop />
                    <ImageProtection />
                    <DateSelectionModal />
                    <CartConfirmation />
                    <WishlistLoginPrompt />
                    <AppRouter />
                  </BuildLookProvider>
                </CartProvider>
              </BookingProvider>
            </SiteTextsProvider>
          </SavedLooksProvider>
        </CustomerAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
